import cn from 'classnames';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useIntl } from 'react-intl';

import {
  MedicalImageRender,
  type FullHeadMPRProjection,
  type FullHeadMPRSession,
} from '@/shared/graphics/medicalImageRender/MedicalImageRender';
import { EnterFullscreenButton } from '@/shared/ui';

import { getGuideAxesName } from '@/entities/assets';

import { FULL_HEAD_MPR_PROJECTION_TO_ORIENTATION } from '@/features/MPR';

import styles from './FullHeadMPRPaneControls.module.scss';

const PAGE_PANE_VIEWER = {
  getPaneLayout: () => MedicalImageRender.getFullHeadMPRPaneLayout(),
  getPrimaryProjection: () =>
    MedicalImageRender.getFullHeadMPRPrimaryProjection(),
  getVisibleProjections: () =>
    MedicalImageRender.getFullHeadMPRVisibleProjections() ?? [],
  getPaneElement: (projection: FullHeadMPRProjection) =>
    MedicalImageRender.getFullHeadMPRPaneElement(projection),
  setPrimaryProjection: (projection: FullHeadMPRProjection) =>
    MedicalImageRender.setFullHeadMPRPrimaryProjection(projection),
};

type SmallPane = {
  element: HTMLDivElement;
  projection: FullHeadMPRProjection;
};

type FullHeadMPRPaneControlsProps = {
  isReady: boolean;
  className?: string;
  session?: FullHeadMPRSession | null;
};

export const FullHeadMPRPaneControls = (
  props: FullHeadMPRPaneControlsProps,
) => {
  const { isReady, className, session } = props;
  const { formatMessage, locale } = useIntl();
  const [smallPanes, setSmallPanes] = useState<SmallPane[]>([]);
  const viewer = session ?? PAGE_PANE_VIEWER;

  useEffect(() => {
    if (!isReady) {
      setSmallPanes([]);
      return;
    }

    const update = () => {
      const projections = viewer.getVisibleProjections();
      const primaryProjection = viewer.getPrimaryProjection();
      projections.forEach((projection) => {
        const element = viewer.getPaneElement(projection);
        if (element) {
          element.setAttribute(
            'aria-label',
            getGuideAxesName(
              FULL_HEAD_MPR_PROJECTION_TO_ORIENTATION[projection],
              formatMessage,
            ),
          );
        }
      });
      const panes =
        viewer.getPaneLayout() === 'one_plus_two' && projections.length > 1
          ? projections.flatMap((projection) => {
              const element = viewer.getPaneElement(projection);
              return element && projection !== primaryProjection
                ? [{ element, projection }]
                : [];
            })
          : [];

      setSmallPanes((previous) =>
        previous.length === panes.length &&
        previous.every(
          (pane, index) =>
            pane.element === panes[index].element &&
            pane.projection === panes[index].projection,
        )
          ? previous
          : panes,
      );
    };

    const removeListener = session
      ? session.addEventListener('fullHeadMPRPaneLayoutChanged', update)
      : MedicalImageRender.addEventListener(
          'fullHeadMPRPaneLayoutChanged',
          update,
          'fullHeadMPR',
        );

    update();

    return () => {
      removeListener?.();
    };
  }, [isReady, locale, session, viewer]);

  const label = formatMessage({
    id: 'mpr.makePrimary',
    defaultMessage: 'Make primary',
  });

  return smallPanes.map(({ element, projection }) =>
    createPortal(
      <div
        className={cn(styles.container, className)}
        onPointerDown={(event) => event.stopPropagation()}
        onMouseDown={(event) => event.stopPropagation()}
        onClick={(event) => event.stopPropagation()}
      >
        <EnterFullscreenButton
          ariaLabel={label}
          className={styles.button}
          iconSize={24}
          onClick={() => viewer.setPrimaryProjection(projection)}
        />
      </div>,
      element,
      projection,
    ),
  );
};
